import { create } from "zustand";

import { analytics } from "~/analytics";
import i18n from "~/lib/i18n";
import type { SupportedLocale } from "~/lib/locale";
import { matchSupported, writeLocaleCookie } from "~/lib/locale";
import { Sentry } from "~/lib/sentry";

export type LocaleStore = {
  locale: SupportedLocale;
  setLocale: (next: SupportedLocale) => void;
};

export const useLocaleStore = create<LocaleStore>()((set, get) => ({
  locale: "en",
  setLocale: (next) => {
    const from = get().locale;
    if (from === next) return;

    void i18n.loadLanguages(next).catch((error: unknown) => {
      Sentry.captureException(error, {
        tags: { component: "useLocaleStore", operation: "loadLanguages" },
      });
    });

    i18n.changeLanguage(next);
    writeLocaleCookie(next);
    document.documentElement.lang = next;
    set({ locale: next });
    analytics.track({
      event: "Language Changed",
      properties: { from, to: next },
    });
  },
}));

export function useLocale() {
  return useLocaleStore((s) => s.locale);
}

/**
 * Applies a raw language preference (e.g. from the API or a form value) to the
 * locale store. No-ops when the preference is empty or unsupported, and
 * `setLocale` itself no-ops when the resolved locale already matches.
 */
export function applyLocalePreference(preference?: string | null): void {
  if (!preference) return;
  const matched = matchSupported(preference);
  if (matched) {
    useLocaleStore.getState().setLocale(matched);
  }
}
